import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

const PROTECTED = [
  "SkillLab: UX Advocacy in a Zero-to-One Feature",
  "SkillLab: Revamping the Counselor Portal",
  "SkillLab's Systemic Accessibility",
  "SkillLab Design System: Governance for a Small Team",
];
import { usePortfolioAuth } from "@/hooks/usePortfolioAuth";
import { canUnlockHere } from "@/protected/crypto";
import Icon from "@/components/base/Icon";

export default function PortfolioGateModal() {
  const { showModal, closeModal, authorize } = usePortfolioAuth();
  const [input, setInput] = useState("");
  const [error, setError] = useState<false | "wrong" | "unavailable">(false);
  const [showPassword, setShowPassword] = useState(false);
  const [visible, setVisible] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (showModal) {
      // remember what opened the dialog so focus can go back to it
      returnFocusRef.current = document.activeElement as HTMLElement | null;
      setVisible(true);
      setInput("");
      setError(false);
      setShowPassword(false);
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 150);
      return () => clearTimeout(timer);
    } else {
      returnFocusRef.current?.focus?.();
      const timer = setTimeout(() => setVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [showModal]);

  // Hide background content from screen readers when modal is open
  useEffect(() => {
    const root = document.getElementById("root");
    if (showModal) {
      root?.setAttribute("aria-hidden", "true");
    } else {
      root?.removeAttribute("aria-hidden");
    }
    return () => {
      root?.removeAttribute("aria-hidden");
    };
  }, [showModal]);

  // Focus trap + Escape to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && showModal) {
        closeModal();
        return;
      }

      if (e.key === "Tab" && showModal && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'input:not([disabled]), button:not([disabled]), [href], select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last?.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first?.focus();
          }
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showModal, closeModal]);

  if (!visible) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await authorize(input);
    if (!success) {
      if (canUnlockHere()) {
        setError("wrong");
        setInput("");
      } else {
        // The password may be right: this page just cannot check it
        setError("unavailable");
      }
    }
  };

  const handleSkip = () => {
    closeModal();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="gate-modal-title"
      aria-describedby="gate-modal-desc"
    >
      <div
        className={`absolute inset-0 bg-black/70 transition-opacity duration-300 ${
          showModal ? "opacity-100" : "opacity-0"
        }`}
        onClick={handleSkip}
      />

      <div
        ref={modalRef}
        className={`relative w-full max-w-md bg-gray-900 border border-gray-800 rounded-lg p-8 shadow-2xl transition-all duration-300 ${
          showModal ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 translate-y-4"
        }`}
      >
        <div className="text-center mb-8">
          <div className="w-14 h-14 flex items-center justify-center mx-auto mb-5 rounded-full bg-gray-800">
            <Icon name="shield" className="text-gray-300 text-2xl" />
          </div>
          <h2 id="gate-modal-title" className="text-2xl font-light text-white mb-3">
            Unlock the SkillLab case studies
          </h2>
          <p id="gate-modal-desc" className="text-gray-300 text-base leading-relaxed">
            One password unlocks all four protected case studies. You only need to enter it once
            while this tab is open.
          </p>
        </div>

        <ul className="mb-8 space-y-2 border-y border-gray-800 py-4" aria-label="Protected case studies">
          {PROTECTED.map((title) => (
            <li key={title} className="flex items-start gap-3 text-gray-200 text-sm leading-snug">
              <Icon name="lock" className="text-gray-300 mt-1" aria-hidden="true" />
              {title}
            </li>
          ))}
        </ul>

        <form onSubmit={handleSubmit} className="space-y-4">
          <label htmlFor="gate-modal-password" className="field-label -mb-2">Password</label>
          <div className="relative">
            <input
              id="gate-modal-password"
              autoComplete="current-password"
              aria-invalid={error === "wrong" || undefined}
              aria-describedby={error ? "gate-modal-error" : undefined}
              ref={inputRef}
              type={showPassword ? "text" : "password"}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                if (error) setError(false);
              }}
              placeholder="Enter password"
              className="field text-sm pr-12"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="btn-icon btn-icon--ghost absolute right-1 top-1/2 -translate-y-1/2"
              aria-label="Show password"
              aria-pressed={showPassword}
            >
              <Icon name={showPassword ? "eye-off" : "eye"} className="text-lg" />
            </button>
          </div>

          {error && (
            <p id="gate-modal-error" role="alert" className="field-error">
              <Icon name="alert-circle" aria-hidden="true" />
              {error === "unavailable"
                ? "This preview can't check passwords. Try it on the published site."
                : "Incorrect password. Please try again."}
            </p>
          )}

          <button
            type="submit"
            disabled={!input.trim()}
            className="btn btn-primary w-full py-3 text-base whitespace-nowrap"
          >
            Unlock case studies
          </button>
        </form>

        <div className="mt-5 flex flex-col items-center gap-3 text-center">
          <button
            type="button"
            onClick={handleSkip}
            className="btn btn-ghost text-sm whitespace-nowrap"
          >
            Not now
          </button>
          <p className="text-gray-300 text-sm">
            No password?{" "}
            <a
              href="mailto:info.lorenzobernardi@proton.me?subject=Portfolio%20password%20request"
              className="link"
            >
              Email me
            </a>{" "}
            and I will send it.
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
}